import React, {useState, useEffect} from "react";
import axios from "axios";
import MemberRow from "./MemberRow";
import FamilyRow from "./FamilyRow";
import InfoCardsManager from './InfoCardsManager';
import FAQManager from './FAQManager';
import CandidatesManager from './admin/CandidatesManager';
import SpoiledPhrasesManager from './admin/SpoiledPhrasesManager';
import ElectionControl from './admin/ElectionControl';
import MenuManager from './admin/MenuManager';
import {electionApi} from '../api/election';
import {menuApi} from '../api/menu';

const API = "/api/admin";

export default function Admin() {
    /* ---------- Авторизация ---------- */
    const [auth, setAuth] = useState({username: "", password: ""});
    const [loggedIn, setLoggedIn] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);   // проверка при загрузке
    const [loginError, setLoginError] = useState("");

    /* ---------- Данные ---------- */
    const [candidatesExpanded, setCandidatesExpanded] = useState(false);
    const [spoiledExpanded, setSpoiledExpanded] = useState(false);
    const [electionExpanded, setElectionExpanded] = useState(true);
    const [members, setMembers] = useState([]);
    const [families, setFamilies] = useState([]);
    const [menuTags, setMenuTags] = useState({});
    const [menuItems, setMenuItems] = useState([]);
    const [stats, setStats] = useState({});
    const [settings, setSettings] = useState({
        wedding_date: "",
        rsvp_deadline: "",
        time: "",
        place: "",
        details: "",
        dress_code: "",
        tg_chat_link: "",
        gathering_time: "",
        ceremony_time: "",
        contact_person: "",
        contact_info: "",
        second_wave_deadline: "",
    });
    const [faqExpanded, setFaqExpanded] = useState(false);
    const [menuExpanded, setMenuExpanded] = useState(false);

    /* ---------- Формы ---------- */
    const [newMember, setNewMember] = useState({
        first_name: "", last_name: "", patronymic: "", gender: "male", email: "", is_second_wave: false,
    });

    const [newFamilyName, setNewFamilyName] = useState("");
    const [selectedMembers, setSelectedMembers] = useState({});
    const [addingToFamilyId, setAddingToFamilyId] = useState(null);
    const [addSelectedMembers, setAddSelectedMembers] = useState({});

    const [showCopyPopup, setShowCopyPopup] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const [infoCardsExpanded, setInfoCardsExpanded] = useState(false);

    /* ---------- Бекап ---------- */
    const [showImportModal, setShowImportModal] = useState(false);
    const [backupFile, setBackupFile] = useState(null);
    const [importStatus, setImportStatus] = useState(null);
    const [isImporting, setIsImporting] = useState(false);

    // ------------------------------------------------------------------
    // Проверка учётных данных (делает реальный запрос к защищённому API)
    // ------------------------------------------------------------------
    const verifyAuth = async (creds) => {
        try {
            await axios.get(`${API}/settings`, {auth: creds});
            return true;
        } catch (error) {
            if (error.response?.status === 401) {
                throw new Error("Неверный логин или пароль");
            }
            throw new Error("Ошибка соединения с сервером");
        }
    };

    // ------------------------------------------------------------------
    // Загрузка всех данных (только после успешной авторизации)
    // ------------------------------------------------------------------
    const loadData = async (creds) => {
        try {
            const [mem, fam, st, sett] = await Promise.all([axios.get(`${API}/members`, {auth: creds}), axios.get(`${API}/families`, {auth: creds}), axios.get(`${API}/stats`, {auth: creds}), axios.get(`${API}/settings`, {auth: creds}),]);

            setMembers(mem.data);
            setFamilies(fam.data);
            setStats(st.data);

            try {
                const tagsRes = await menuApi.getMemberTags({auth: creds});
                const tagMap = {};
                tagsRes.data.forEach((t) => { tagMap[t.member_id] = t; });
                setMenuTags(tagMap);
            } catch (e) {
                setMenuTags({});
            }

            try {
                const itemsRes = await menuApi.getItems({auth: creds});
                setMenuItems(itemsRes.data || []);
            } catch (e) {
                setMenuItems([]);
            }

            const s = sett.data;
            setSettings({
                wedding_date: s.wedding_date?.slice(0, 10) || "",
                rsvp_deadline: s.rsvp_deadline?.slice(0, 10) || "",
                time: s.time || "",
                place: s.place || "",
                details: s.details || "",
                dress_code: s.dress_code || "",
                tg_chat_link: s.tg_chat_link || "",
                gathering_time: s.gathering_time || "",
                ceremony_time: s.ceremony_time || "",
                contact_person: s.contact_person || "",
                contact_info: s.contact_info || "",
                second_wave_deadline: s.second_wave_deadline ? s.second_wave_deadline.slice(0, 10) : "",
            });
        } catch (e) {
            // Если данные не загрузились (например, сессия истекла) – разлогиниваем
            localStorage.removeItem("weddingAdminAuth");
            setLoggedIn(false);
            throw e;
        }
    };

    // ------------------------------------------------------------------
    // При загрузке страницы проверяем сохранённые данные
    // ------------------------------------------------------------------
    useEffect(() => {
        const saved = localStorage.getItem("weddingAdminAuth");
        if (!saved) {
            setIsVerifying(false);
            return;
        }

        const creds = JSON.parse(saved);
        setAuth(creds);

        verifyAuth(creds)
            .then(() => {
                setLoggedIn(true);
                return loadData(creds);
            })
            .catch(() => {
                // неверные данные в localStorage – удаляем
                localStorage.removeItem("weddingAdminAuth");
                setLoggedIn(false);
                setLoginError("Сессия устарела, войдите заново");
            })
            .finally(() => setIsVerifying(false));
    }, []);

    // ------------------------------------------------------------------
    // Логин с проверкой
    // ------------------------------------------------------------------
    const login = async () => {
        setLoginError("");
        try {
            await verifyAuth(auth);
            // успех – сохраняем и загружаем
            localStorage.setItem("weddingAdminAuth", JSON.stringify(auth));
            setLoggedIn(true);
            await loadData(auth);
        } catch (err) {
            setLoginError(err.message);
        }
    };

    // ------------------------------------------------------------------
    // Остальные CRUD‑операции (без изменений, но используем config)
    // ------------------------------------------------------------------
    const config = loggedIn ? {auth} : {};

    const createMember = async () => {
        if (!newMember.first_name.trim() || !newMember.last_name.trim()) {
            alert("Заполните фамилию и имя");
            return;
        }
        try {
            const res = await axios.post(`${API}/members`, newMember, config);
            setMembers([...members, res.data]);
            setNewMember({
                first_name: "", last_name: "", patronymic: "", gender: "male", email: "", is_second_wave: false
            });
        } catch (e) {
            alert(e.response?.data?.detail || "Ошибка создания участника");
        }
    };

    const createFamily = async () => {
        const membersList = Object.entries(selectedMembers)
            .filter(([_, can]) => can != null)
            .map(([id, can_vote_for_others]) => ({
                member_id: +id, can_vote_for_others,
            }));

        if (membersList.length === 0) {
            alert("Выберите хотя бы одного участника");
            return;
        }

        try {
            const res = await axios.post(`${API}/families`, {
                name: newFamilyName || "Новая семья", description: "", members: membersList,
            }, config);
            setFamilies([...families, res.data]);
            setNewFamilyName("");
            setSelectedMembers({});
        } catch (e) {
            alert(e.response?.data?.detail || "Ошибка создания семьи");
        }
    };

    const addMembersToFamily = async (familyId) => {
        const membersList = Object.entries(addSelectedMembers)
            .filter(([_, can]) => can != null)
            .map(([id, can_vote_for_others]) => ({
                member_id: +id, can_vote_for_others,
            }));

        try {
            for (const link of membersList) {
                await axios.post(`${API}/families/${familyId}/members`, link, config);
            }
            await loadData(auth);
            setAddingToFamilyId(null);
            setAddSelectedMembers({});
        } catch (e) {
            alert(e.response?.data?.detail || "Ошибка добавления участников");
        }
    };

    const saveSettings = async () => {
        try {
            const payload = {
                wedding_date: settings.wedding_date,
                rsvp_deadline: settings.rsvp_deadline,
                time: settings.time,
                place: settings.place,
                details: settings.details,
                dress_code: settings.dress_code,
                tg_chat_link: settings.tg_chat_link,
                gathering_time: settings.gathering_time,
                ceremony_time: settings.ceremony_time,
                contact_person: settings.contact_person,
                contact_info: settings.contact_info,
                second_wave_deadline: settings.second_wave_deadline || null,
            };

            const res = await axios.put(`${API}/settings`, payload, config);
            alert("Параметры сохранены");
            const s = res.data;
            setSettings({
                wedding_date: s.wedding_date?.slice(0, 10) || "",
                rsvp_deadline: s.rsvp_deadline?.slice(0, 10) || "",
                time: s.time || "",
                place: s.place || "",
                details: s.details || "",
                dress_code: s.dress_code || "",
                tg_chat_link: s.tg_chat_link || "",
                gathering_time: s.gathering_time || "",
                ceremony_time: s.ceremony_time || "",
                contact_person: s.contact_person || "",
                contact_info: s.contact_info || "",
                second_wave_deadline: s.second_wave_deadline ? s.second_wave_deadline.slice(0, 10) : "",
            });
        } catch (e) {
            alert(e.response?.data?.detail || "Не удалось сохранить параметры");
        }
    };

    const copyLink = (token) => {
        navigator.clipboard.writeText(`http://bez-gorko.ru/?token=${token}`);
        setShowCopyPopup(true);
        setTimeout(() => setShowCopyPopup(false), 3000);
    };

    const handleExportBackup = async () => {
        try {
            const response = await axios.get(`${API}/backup/export`, {
                responseType: 'blob', auth
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `wedding_backup_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setShowCopyPopup(true);
            setTimeout(() => setShowCopyPopup(false), 3000);
        } catch (error) {
            alert('Ошибка при создании бекапа: ' + (error.response?.data?.detail || error.message));
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.name.match(/\.(xlsx|xls)$/i)) {
                alert('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
                return;
            }
            setBackupFile(file);
        }
    };

    const handleImportBackup = async () => {
        if (!backupFile) {
            alert('Пожалуйста, выберите файл для загрузки');
            return;
        }
        if (!window.confirm('ВНИМАНИЕ: Загрузка бекапа полностью перезапишет все данные в базе. Продолжить?')) {
            return;
        }
        setIsImporting(true);
        setImportStatus(null);

        const formData = new FormData();
        formData.append('file', backupFile);

        try {
            const response = await axios.post(`${API}/backup/import`, formData, {
                auth, headers: {'Content-Type': 'multipart/form-data'}
            });
            setImportStatus({
                success: true, message: response.data.message, statistics: response.data.statistics
            });
            await loadData(auth);
            setBackupFile(null);
        } catch (error) {
            setImportStatus({
                success: false, message: error.response?.data?.detail || 'Ошибка при импорте'
            });
        } finally {
            setIsImporting(false);
        }
    };

    const handleResetVote = async () => {
        if (!window.confirm('Сбросить голосование? Все туры и голоса будут удалены. Кандидаты и фразы останутся.')) return;
        try {
            await electionApi.resetElection(auth);
            alert('Голосование сброшено');
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сброса');
        }
    };

    // Пока идёт проверка при загрузке – показываем спиннер
    if (isVerifying) {
        return (<div className="min-h-screen flex items-center justify-center bg-light-blue">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-accent"></div>
        </div>);
    }

    if (!loggedIn) {
        return (<div className="min-h-screen flex items-center justify-center bg-light-blue">
            <div className="bg-white p-8 rounded-lg shadow-lg w-80">
                <h1 className="text-2xl mb-4">Админ‑панель</h1>
                {loginError && (<div className="mb-4 p-2 bg-red-100 text-red-700 rounded text-sm">
                    {loginError}
                </div>)}
                <input
                    className="block w-full p-2 mb-3 border rounded"
                    placeholder="Логин"
                    value={auth.username}
                    onChange={(e) => setAuth({...auth, username: e.target.value})}
                />
                <input
                    className="block w-full p-2 mb-3 border rounded"
                    type="password"
                    placeholder="Пароль"
                    value={auth.password}
                    onChange={(e) => setAuth({...auth, password: e.target.value})}
                />
                <button
                    onClick={login}
                    className="w-full bg-gold-accent text-white py-2 rounded hover:bg-yellow-600"
                >
                    Войти
                </button>
            </div>
        </div>);
    }

    // ------------------------------------------------------------------
    // Рендер админки (весь остальной код без изменений)
    // ------------------------------------------------------------------
    return (<div className="container mx-auto p-6 bg-light-blue min-h-screen">
        <h1 className="text-4xl font-display text-center mb-8">Свадебная админка</h1>

        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl">Всего семей</h3>
                <p className="text-4xl font-bold text-gold-accent">{stats.total_families || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl">Придут</h3>
                <p className="text-4xl font-bold text-green-600">{stats.attending || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl">Не придут</h3>
                <p className="text-4xl font-bold text-red-600">{stats.not_attending || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl">Не ответили</h3>
                <p className="text-4xl font-bold text-yellow-600">{stats.pending || 0}</p>
            </div>
        </div>

        {/* Параметры мероприятия (свёртываемый блок) */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setSettingsExpanded(!settingsExpanded)}>
                <h2 className="text-2xl">Параметры мероприятия</h2>
                <svg className={`w-6 h-6 transform transition-transform ${settingsExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {settingsExpanded && (<div className="mt-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="text-gray-700">Дата свадьбы</span>
                        <input type="date" className="w-full p-2 border rounded mt-1"
                               value={settings.wedding_date}
                               onChange={(e) => setSettings({...settings, wedding_date: e.target.value})}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Дедлайн RSVP</span>
                        <input type="date" className="w-full p-2 border rounded mt-1"
                               value={settings.rsvp_deadline}
                               onChange={(e) => setSettings({...settings, rsvp_deadline: e.target.value})}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Дедлайн RSVP для второй очереди</span>
                        <input type="date" className="w-full p-2 border rounded mt-1"
                               value={settings.second_wave_deadline || ''} onChange={(e) => setSettings({
                            ...settings, second_wave_deadline: e.target.value
                        })}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Время сбора гостей</span>
                        <input type="time" className="w-full p-2 border rounded mt-1"
                               value={settings.gathering_time}
                               onChange={(e) => setSettings({...settings, gathering_time: e.target.value})}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Начало торжества</span>
                        <input type="time" className="w-full p-2 border rounded mt-1"
                               value={settings.ceremony_time}
                               onChange={(e) => setSettings({...settings, ceremony_time: e.target.value})}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Место проведения</span>
                        <input type="text" className="w-full p-2 border rounded mt-1" value={settings.place}
                               onChange={(e) => setSettings({...settings, place: e.target.value})}/>
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-gray-700">Особенности / детали</span>
                        <textarea className="w-full p-2 border rounded mt-1" rows={3} value={settings.details}
                                  onChange={(e) => setSettings({...settings, details: e.target.value})}/>
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-gray-700">Дресс-код</span>
                        <input type="text" className="w-full p-2 border rounded mt-1"
                               value={settings.dress_code}
                               onChange={(e) => setSettings({...settings, dress_code: e.target.value})}/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Остались вопросы?</span>
                        <input type="text" className="w-full p-2 border rounded mt-1"
                               value={settings.contact_person}
                               onChange={(e) => setSettings({...settings, contact_person: e.target.value})}
                               placeholder="Имя"/>
                    </label>
                    <label className="block">
                        <span className="text-gray-700">Ссылка на ТГ</span>
                        <input type="text" className="w-full p-2 border rounded mt-1"
                               value={settings.contact_info}
                               onChange={(e) => setSettings({...settings, contact_info: e.target.value})}
                               placeholder="@username или телефон"/>
                    </label>
                    <label className="block md:col-span-2">
                        <span className="text-gray-700">Ссылка на Telegram-чат для гостей</span>
                        <input type="text" className="w-full p-2 border rounded mt-1"
                               value={settings.tg_chat_link || ""}
                               onChange={(e) => setSettings({...settings, tg_chat_link: e.target.value})}/>
                    </label>
                </div>
                <button onClick={saveSettings}
                        className="mt-6 w-full bg-gold-accent text-white py-2 rounded hover:bg-yellow-600">Сохранить
                    параметры
                </button>
            </div>)}
        </div>

        {/* Резервное копирование */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h2 className="text-2xl mb-4">Резервное копирование</h2>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Скачать бекап</h3>
                    <p className="text-gray-600 text-sm mb-4">Скачайте полную копию всех данных в формате Excel.
                        Файл содержит все настройки, гостей и семьи.</p>
                    <button onClick={handleExportBackup}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        Скачать бекап
                    </button>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Загрузить бекап</h3>
                    <p className="text-gray-600 text-sm mb-4">Загрузите ранее сохраненный бекап. <strong
                        className="text-red-600">ВНИМАНИЕ:</strong> это полностью перезапишет все данные!</p>
                    <button onClick={() => setShowImportModal(true)}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
                        </svg>
                        Загрузить бекап
                    </button>
                </div>
            </div>
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                    <div className="ml-3"><p className="text-sm text-yellow-700"><strong>Важно:</strong> Регулярно
                        скачивайте бекапы перед обновлениями. Загрузка бекапа заменяет ВСЕ данные в системе.</p>
                    </div>
                </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">Бекап сохраняется в формате Excel с отдельными листами для
                настроек, гостей, семей и связей.</p>
        </div>
        {/* Блок кандидатов */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setCandidatesExpanded(!candidatesExpanded)}>
                <h2 className="text-2xl">Потенциальные женихи</h2>
                <svg className={`w-6 h-6 transform transition-transform ${candidatesExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {candidatesExpanded && (
                <div className="mt-6">
                    <CandidatesManager
                        auth={auth}
                        members={members.filter(m => m.gender === 'male')}   // ТОЛЬКО МУЖЧИНЫ
                    />
                </div>
            )}
        </div>

        {/* Блок испорченных фраз */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setSpoiledExpanded(!spoiledExpanded)}>
                <h2 className="text-2xl">Испорченный бюллетень</h2>
                <svg className={`w-6 h-6 transform transition-transform ${spoiledExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {spoiledExpanded && <div className="mt-6"><SpoiledPhrasesManager auth={auth}/></div>}
        </div>

        {/* Блок управления голосованием */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between">
                <div className="flex items-center justify-between cursor-pointer flex-1 mr-4"
                     onClick={() => setElectionExpanded(!electionExpanded)}>
                    <h2 className="text-2xl">Управление голосованием</h2>
                    <svg className={`w-6 h-6 transform transition-transform ${electionExpanded ? 'rotate-180' : ''}`}
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </div>
                <button onClick={handleResetVote}
                        className="bg-red-800 text-white px-4 py-2 rounded hover:bg-red-900">
                    Сбросить голосование
                </button>
            </div>
            {electionExpanded && <div className="mt-6"><ElectionControl auth={auth}/></div>}
        </div>

        {/* Блок опроса по меню */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setMenuExpanded(!menuExpanded)}>
                <h2 className="text-2xl">🍽️ Меню (опрос гостей)</h2>
                <svg className={`w-6 h-6 transform transition-transform ${menuExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {menuExpanded && <div className="mt-6"><MenuManager auth={auth}/></div>}
        </div>
        {/* Инфо-карточки */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setInfoCardsExpanded(!infoCardsExpanded)}>
                <h2 className="text-2xl">📋 Инфо-карточки (правила площадки)</h2>
                <svg className={`w-6 h-6 transform transition-transform ${infoCardsExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {infoCardsExpanded && <div className="mt-6"><InfoCardsManager auth={auth}/></div>}
        </div>

        {/* FAQ */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex items-center justify-between cursor-pointer"
                 onClick={() => setFaqExpanded(!faqExpanded)}>
                <h2 className="text-2xl">❓ Вопросы и ответы (Q&A)</h2>
                <svg className={`w-6 h-6 transform transition-transform ${faqExpanded ? 'rotate-180' : ''}`}
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
            </div>
            {faqExpanded && <div className="mt-6"><FAQManager auth={auth}/></div>}
        </div>

        {/* Добавление новых записей */}
        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-2xl mb-4">Добавить человека</h2>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <input className="w-full p-2 border rounded" placeholder="Фамилия" value={newMember.last_name}
                           onChange={(e) => setNewMember({...newMember, last_name: e.target.value})}/>
                    <input className="w-full p-2 border rounded" placeholder="Имя" value={newMember.first_name}
                           onChange={(e) => setNewMember({...newMember, first_name: e.target.value})}/>
                </div>
                <input className="w-full p-2 border rounded mb-3" placeholder="Отчество (необязательно)"
                       value={newMember.patronymic}
                       onChange={(e) => setNewMember({...newMember, patronymic: e.target.value})}/>
                <select className="w-full p-2 border rounded mb-3" value={newMember.gender}
                        onChange={(e) => setNewMember({...newMember, gender: e.target.value})}>
                    <option value="male">Мужской</option>
                    <option value="female">Женский</option>
                </select>
                <input className="w-full p-2 border rounded mb-3" placeholder="E‑mail (необязательно)"
                       value={newMember.email}
                       onChange={(e) => setNewMember({...newMember, email: e.target.value})}/>
                <div className="mb-3 flex items-center">
                    <input type="checkbox" id="is_second_wave" checked={newMember.is_second_wave || false}
                           onChange={(e) => setNewMember({...newMember, is_second_wave: e.target.checked})}
                           className="mr-2"/>
                    <label htmlFor="is_second_wave">Донабор (вторая очередь)</label>
                </div>
                <button onClick={createMember}
                        className="bg-gold-accent text-white px-6 py-2 rounded hover:bg-yellow-600">+ Добавить
                </button>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-2xl mb-4">Создать семью / группу</h2>
                <input className="w-full p-3 border rounded mb-4" placeholder="Название семьи (необязательно)"
                       value={newFamilyName} onChange={(e) => setNewFamilyName(e.target.value)}/>
                <div className="max-h-64 overflow-y-auto space-y-2 mb-4">
                    {members.map((m) => (
                        <label key={m.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                            <span>{m.last_name} {m.first_name} {m.patronymic || ""}</span>
                            <div className="flex items-center space-x-2">
                                <input type="checkbox" checked={selectedMembers[m.id] != null}
                                       onChange={(e) => setSelectedMembers({
                                           ...selectedMembers, [m.id]: e.target.checked ? false : null
                                       })}/>
                                <span className="ml-1">включить</span>
                                <input type="checkbox" checked={selectedMembers[m.id] === true}
                                       disabled={selectedMembers[m.id] == null}
                                       onChange={(e) => setSelectedMembers({
                                           ...selectedMembers, [m.id]: e.target.checked ? true : false
                                       })}/>
                                <span className="ml-1">отвечает за всех</span>
                            </div>
                        </label>))}
                </div>
                <button onClick={createFamily}
                        className="w-full bg-gold-accent text-white py-3 rounded text-lg hover:bg-yellow-600">Создать
                    приглашение
                </button>
            </div>
        </div>

        {/* Список семей */}
        <div className="mt-10">
            <h2 className="text-3xl mb-6">Все семьи</h2>
            {families.map((f) => (<FamilyRow
                key={f.id}
                family={f}
                members={members}
                menuTags={menuTags}
                menuItems={menuItems}
                reload={() => loadData(auth)}
                copyLink={copyLink}
                setAddingToFamilyId={setAddingToFamilyId}
                addingToFamilyId={addingToFamilyId}
                addSelectedMembers={addSelectedMembers}
                setAddSelectedMembers={setAddSelectedMembers}
                addMembersToFamily={addMembersToFamily}
            />))}
        </div>

        {/* Попап копирования ссылки */}
        {showCopyPopup && (<div
            className="fixed top-4 right-4 bg-green-500 text-white p-4 rounded shadow-lg flex items-center z-50">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
            </svg>
            <p>Ссылка скопирована!</p>
            <button onClick={() => setShowCopyPopup(false)} className="ml-4 text-white hover:text-gray-200">×
            </button>
        </div>)}

        {/* Модальное окно импорта бекапа */}
        {showImportModal && (<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 text-red-600">⚠️ ВНИМАНИЕ!</h3>
                    <p className="text-gray-700 mb-4">Загрузка бекапа <strong>полностью перезапишет</strong> всю
                        базу данных:</p>
                    <ul className="list-disc pl-5 text-gray-600 mb-6 space-y-1">
                        <li>Все текущие настройки будут заменены</li>
                        <li>Все гости будут удалены и заменены</li>
                        <li>Все семьи будут удалены и заменены</li>
                        <li>Все существующие ссылки приглашений могут перестать работать</li>
                    </ul>
                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">Выберите файл бекапа (.xlsx):</label>
                        <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect}
                               className="w-full p-2 border border-gray-300 rounded" disabled={isImporting}/>
                        {backupFile &&
                            <p className="text-sm text-green-600 mt-2">✓ Выбран файл: {backupFile.name}</p>}
                    </div>
                    {importStatus && (<div
                        className={`p-3 rounded mb-4 ${importStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {importStatus.message}
                        {importStatus.statistics && <div
                            className="mt-2 text-sm">Загружено: {importStatus.statistics.members} гостей, {importStatus.statistics.families} семей</div>}
                    </div>)}
                    <div className="flex justify-end space-x-3">
                        <button onClick={() => {
                            setShowImportModal(false);
                            setBackupFile(null);
                            setImportStatus(null);
                        }} className="px-4 py-2 text-gray-600 hover:text-gray-800" disabled={isImporting}>Отмена
                        </button>
                        <button onClick={handleImportBackup} disabled={!backupFile || isImporting}
                                className={`px-4 py-2 rounded ${!backupFile || isImporting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}>
                            {isImporting ? 'Загрузка...' : 'Заменить все данные'}
                        </button>
                    </div>
                </div>
            </div>
        </div>)}
    </div>);
}